import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDocumentMergeFields } from "../store/actions/documentActions";
import { TYPE_PROPOSAL, SYSTEM, CUSTOM, OTHERS } from "../constants/editor";
import { formatProviderDisplayName, setGMTAtSession, toProperCase } from "../_helpers/utils";
import * as moment from "moment";
import { DOCUMENT_DETAILS_FETCH_SUCCESS, PUBLISHED_DOCUMENT_FETCH_SUCCESS } from "../store/actions/actionTypes";
import { fetchZohoCrmFieldsSchema } from "../store/actions/companyActions";
import { fetchUserSchema } from "../store/actions/authActions";
import useModuleName from "./useModuleName";

// Simple DOM updater
const useMergeFieldsHydration = ({ origin, companyId }) => {
  const dispatch = useDispatch();
  // const { mfSchemaData, fetchFromPreview, documentDetails } = useSelector(state => state.document);
  const { documentDetails } = useSelector(state => state.document);
  const actionType = useSelector(state => state.company?.type);
  const countries = useSelector(state => state.resources?.countries);
  const userId = useSelector(state => state.auth?.uid) || documentDetails?.owner;
  const templateDetails = useSelector(state => state?.template?.templateDetails);
  const companyTemplateDetails = useSelector(state => state?.company?.companyTemplateInfo);
  const layoutDetails = useSelector(state => state?.ai?.companyLayoutInfo);
  const thirdPartyModules = useSelector(state => state?.company?.zohoCrmModulesSchema);
  const getName = useModuleName({ provider: documentDetails?.data?.data_source?.provider });
  const [ready, setReady] = useState(false);
  const [userInfo, setUserInfo] = useState(false);

  const DOCHIPO_SYSTEM = {
    module_fields_map: {
      ["documents"]: [
        { name: "Document Owner", slug: "document-owner", fields: ["document-owner"] },
        { name: "Document Name", slug: "document-name", fields: ["document-name"] },
        { name: "Description", slug: "description", fields: ["description"] },
        { name: "Created Time", slug: "created-time", fields: ["created-time"], special_field: true },
      ],

      ["document-owner"]: [
        { name: "First Name", slug: "firstname", fields: ["firstname"] },
        { name: "Middle Name", slug: "middlename", fields: ["middlename"] },
        { name: "Last Name", slug: "lastname", fields: ["lastname"] },
        { name: "Email", slug: "email", fields: ["email"] },
        { name: "Mobile", slug: "mobile", fields: ["mobile-isd-code", "mobile"], special_field: true },
        { name: "Phone", slug: "phone", fields: ["phone-isd-code", "phone", "phone-extension"], special_field: true },
        { name: "Address Line 1", slug: "street", fields: ["street"] },
        { name: "Address Line 2", slug: "addressline2", fields: ["addressline2"] },
        { name: "Country", slug: "country-code", fields: ["country-code"], special_field: true },
        { name: "State/Province", slug: "state", fields: ["state"] },
        { name: "City", slug: "city", fields: ["city"] },
        { name: "Zip/Postal Code", slug: "zip", fields: ["zip"] },
        { name: "Time Zone", slug: "time-zone", fields: ["time-zone"] },
        { name: "Date Format", slug: "date-format", fields: ["date-format"] },
        { name: "Time Format", slug: "time-format", fields: ["time-format"] },
      ],

      ["company"]: [
        { name: "Company Name", slug: "company-name", fields: ["company-name"] },
        { name: "Alias", slug: "alias", fields: ["alias"] },
        { name: "Phone", slug: "phone", fields: ["phone-isd-code", "phone", "phone-extension"], special_field: true },
        { name: "Fax", slug: "fax", fields: ["fax-isd-code", "fax"], special_field: true },
        { name: "Website", slug: "website", fields: ["website"] },
        { name: "Address Line 1", slug: "street", fields: ["street"] },
        { name: "Address Line 2", slug: "addressline2", fields: ["addressline2"] },
        { name: "Country", slug: "country-code", fields: ["country-code"], special_field: true },
        { name: "State/Province", slug: "state", fields: ["state"] },
        { name: "City", slug: "city", fields: ["city"] },
        { name: "Zip/Postal Code", slug: "zip", fields: ["zip"] },
        { name: "Time Zone", slug: "time-zone", fields: ["time-zone"] },
        { name: "Date Format", slug: "date-format", fields: ["date-format"] },
        { name: "Time Format", slug: "time-format", fields: ["time-format"] },
      ],
    },

    get_country_details_by_iso_code: ISO => {
      const countryObj = countries?.find(country => country.iso_code === ISO);
      return countryObj ?? { name: "", iso_code: "", isd_code: "", cluster_code: "" };
    },

    get_mfs: data => {
      const processedData = data.map(module => {
        const fieldObj = {};
        (module?.merge_fields || []).forEach(field => {
          fieldObj[field?.slug] = field?.value;
        });

        const fields = [];
        DOCHIPO_SYSTEM.module_fields_map?.[module?.slug]?.forEach(field => {
          let value = "";
          const { name: fieldName, slug: fieldSlug, fields: fieldGroup, special_field: isSpecialField } = field;
          const isGroupField = fieldGroup.length > 1;

          if (!isGroupField && !isSpecialField) {
            value = fieldObj?.[fieldSlug];
          }

          if (!isGroupField && isSpecialField) {
            if (fieldSlug === "created-time") {
              // convert epoch seconds to human readable date-time
              value = _helpers.formatDateTime(fieldObj?.[fieldSlug]);
            }
            if (fieldSlug === "country-code") {
              // get country name by iso code
              value = DOCHIPO_SYSTEM.get_country_details_by_iso_code(fieldObj?.[fieldSlug]).name;
            }
          }

          if (isGroupField && isSpecialField) {
            if (["fax", "phone", "mobile"].includes(fieldSlug)) {
              const ISO = fieldObj?.[fieldGroup?.[0]];
              const ISD = DOCHIPO_SYSTEM.get_country_details_by_iso_code(ISO).isd_code;
              const NUMBER = fieldObj?.[fieldGroup?.[1]];
              const EXTN = fieldObj?.[fieldGroup?.[2]];

              const ISD_CODE = !_helpers.isDataEmpty(ISD) ? `+${ISD} ` : "";
              const EXTENSION = !_helpers.isDataEmpty(EXTN) ? ` x ${EXTN}` : "";
              value = !_helpers.isDataEmpty(NUMBER) ? `${ISD_CODE}${NUMBER}${EXTENSION}` : "";
            }
          }

          fields.push({
            isEmpty: _helpers.isDataEmpty(value), // set is empty props
            name: fieldName,
            slug: fieldSlug,
            value,
          });
        });

        return {
          ...module,
          merge_fields: fields,
        };
      });

      return processedData;
    },
  };

  const THIRD_PARTY_APPS = {
    ZOHO_CRM: {
      excluded_fields: [
        "id",
        "Tag",
        "Currency",
        "Exchange_Rate",
        "Lead_Conversion_Time",
        "Sales_Cycle_Duration",
        "Overall_Sales_Duration",
      ],

      excluded_data_types: ["textarea", "fileupload", "imageupload", "subform", "territories", "rollup_summary"],

      direct_mapping_data_types: [
        "text",
        "email",
        "integer",
        "bigint",
        "double",
        "boolean",
        "currency",
        "percent",
        "date",
        "datetime",
        "formula",
        "website",
        "phone",
        "picklist",
        "autonumber",
      ],

      get_mfs: async data => {
        const processedData = await Promise.all(
          Object.keys(data).map(async module => {
            const moduleName = toProperCase(module);
            const moduleSchema = await dispatch(fetchZohoCrmFieldsSchema({ moduleName }));

            const schemaObj = {};
            moduleSchema?.result?.forEach(field => {
              const { api_name, data_type, field_label } = field;
              schemaObj[api_name] = { data_type, field_label };
            });

            const fields = [];
            for (const [api_name, value] of Object.entries(data[module])) {
              const { data_type = null, field_label = null } = schemaObj?.[api_name] ?? {};
              if (
                !api_name.startsWith("$") &&
                !api_name.includes("__") &&
                !THIRD_PARTY_APPS.ZOHO_CRM.excluded_fields.includes(api_name) &&
                !THIRD_PARTY_APPS.ZOHO_CRM.excluded_data_types.includes(data_type)
              ) {
                let formattedVal;

                if (THIRD_PARTY_APPS.ZOHO_CRM.direct_mapping_data_types.includes(data_type)) {
                  if (["date", "datetime"].includes(data_type)) {
                    formattedVal = _helpers.parseDateTime({ isoStr: value, dataType: data_type });
                  } else formattedVal = value;
                } else if (["lookup", "userlookup", "ownerlookup"].includes(data_type)) {
                  formattedVal = value?.name;
                } else if (["multiselectpicklist"].includes(data_type)) {
                  formattedVal = value?.join(", ");
                } else if (["multiselectlookup"].includes(data_type)) {
                  formattedVal = value?.map(item => item?.[api_name]?.name)?.join(", ");
                }

                fields.push({
                  isEmpty: _helpers.isDataEmpty(formattedVal), // set is empty props
                  name: field_label,
                  slug: api_name,
                  value: formattedVal,
                });
              }
            }

            return {
              name: getName(moduleName),
              slug: module.toLowerCase(),
              merge_fields: fields,
            };
          })
        );

        return processedData;
      },
    },
  };

  const _helpers = {
    updateAllMfsWithLatestData: schemaData => {
      const { system: systemData = [], other: otherData = [] } = schemaData;

      const getLatestData = ({ mfType, mfModule, mfField, mfData }) => {
        if (mfType === SYSTEM.SLUG) {
          const moduleObj = systemData?.find(e => e?.slug === mfModule);
          const fieldObj = moduleObj?.merge_fields?.find(e => e?.slug === mfField);
          return fieldObj ?? { isEmpty: null, name: null, slug: null, value: null };
        }
        if (mfType === CUSTOM.SLUG) {
          return { isEmpty: _helpers.isDataEmpty(mfData), name: null, slug: null, value: mfData };
        }
        if (mfType === OTHERS.SLUG) {
          if (
            ![TYPE_PROPOSAL].includes(
              (documentDetails?.type || documentDetails?.data?.template_info?.type).toLowerCase()
            )
          )
            return;
          if (otherData?.length > 0) {
            const moduleObj = otherData?.find(e => e?.slug === mfModule);
            const fieldObj = moduleObj?.merge_fields?.find(e => e?.slug === mfField);
            return fieldObj
              ? { ...fieldObj, moduleName: moduleObj.name }
              : { isEmpty: null, name: null, slug: null, value: null, moduleName: null };
          } else {
            // retain last saved data if third party integration is disabled
            return {
              isEmpty: _helpers.isDataEmpty(mfData),
              name: mfField,
              slug: mfField,
              value: mfData,
              moduleName: mfModule,
            };
          }
        }
      };

      const allMfs = _helpers.getUniqueMergeFields({ selector: ".merge-field" });

      allMfs.forEach(mf => {
        const {
          isEmpty,
          name: newFieldName,
          value: newFieldValue,
          moduleName: newModuleName,
        } = getLatestData({ ...mf.dataset });

        // for third party apps (Omfs)
        if (
          mf.dataset.mfType === OTHERS.SLUG &&
          [TYPE_PROPOSAL].includes((documentDetails?.type || documentDetails?.data?.template_info?.type).toLowerCase())
        ) {
          const { data_source: { provider: dataSource = null } = {} } = documentDetails?.data;
          if (dataSource === "zoho_crm" && newModuleName) {
            mf.dataset.mfLabel = `${toProperCase(newModuleName)}.${newFieldName}`;
          }
        }

        mf.innerText = isEmpty ? mf.dataset.mfLabel : newFieldValue;
        mf.dataset.mfData = isEmpty ? "NULL" : newFieldValue;
        mf.classList.toggle("empty", isEmpty);
        mf.classList.toggle("filled", !isEmpty);
      });
    },

    getUniqueMergeFields: ({ selector }) => {
      return [...new Map([...document.querySelectorAll(selector)].map(node => [node.id, node])).values()];
    },

    getFormattedSchemaData: async schemaData => {
      const formattedData = JSON.parse(JSON.stringify(schemaData));
      const { system: systemData = [], custom: customData = [], other: otherData = [] } = formattedData;

      // format sysyem schemaData
      formattedData[SYSTEM.SLUG] = DOCHIPO_SYSTEM.get_mfs(systemData);

      // format custom schemaData
      for (const field of customData) {
        field.isEmpty = _helpers.isDataEmpty(field.value); // set is empty props
      }

      // format other schemaData
      if (
        [TYPE_PROPOSAL].includes(
          (documentDetails?.type || documentDetails?.data?.template_info?.type)?.toLowerCase()
        ) &&
        otherData
      ) {
        const dataSource = documentDetails?.data_source?.provider || documentDetails?.data?.data_source?.provider;
        if (dataSource === "zoho_crm") {
          formattedData[OTHERS.SLUG] = await THIRD_PARTY_APPS.ZOHO_CRM.get_mfs(otherData);
        }
      }

      return formattedData;
    },

    formatDateTime: epochMs => {
      if (!userInfo?.time_zone) return;
      const dateFormat = {
        "DD-MM-YYYY": "ddd DD MMM",
        "MM-DD-YYYY": "ddd MMM DD",
      };

      const defaultFormats = {
        "date-time": `${dateFormat[userInfo?.date_format] || "MMM ddd DD"}, yyyy ${
          userInfo?.time_format?.substring(0, 2) == 24 ? "HH:mm" : "hh:mm A"
        }`,
      };

      const applicableFormat = defaultFormats["date-time"];

      const gmtValue = setGMTAtSession(userInfo?.time_zone);
      const resultDate = moment(epochMs * 1000)
        .utcOffset(gmtValue)
        .format(applicableFormat);

      return resultDate;
    },

    formatDateOnly: epochMs => {
      if (!userInfo?.time_zone) return;

      const dateFormat = {
        "DD-MM-YYYY": "ddd DD MMM, yyyy",
        "MM-DD-YYYY": "ddd MMM DD, yyyy",
      };

      const applicableFormat = dateFormat[userInfo?.date_format] || "MMM ddd DD, yyyy";

      const gmtValue = setGMTAtSession(userInfo?.time_zone);

      const resultDate = moment(epochMs * 1000)
        .utcOffset(gmtValue)
        .format(applicableFormat);

      return resultDate;
    },

    parseDateTime: ({ isoStr, dataType }) => {
      if (!isoStr) return isoStr;
      const _parseEngines = {
        date: _helpers.formatDateOnly,
        datetime: _helpers.formatDateTime,
      };
      const _pEngine = _parseEngines[dataType];
      if (!_pEngine) return isoStr;

      // convert ISO time string to epoch time (ms)
      const epochMs = Date.parse(isoStr) / 1000;

      // convert epoch seconds to human readable date-time
      const result = _pEngine(epochMs);

      if (epochMs === NaN || result === "Invalid date") return null;
      else return result;
    },

    isDataEmpty: data => {
      const isEmpty = ["", "NULL", null, undefined, "null", "null.null"].includes(data);
      return isEmpty;
    },
  };

  const firstFetchPostActions = async mfSchemaData => {
    // for first time fetch of mfSchemaData
    const formattedSchemaData = await _helpers.getFormattedSchemaData(mfSchemaData);
    _helpers.updateAllMfsWithLatestData(formattedSchemaData);
    requestAnimationFrame(() => {
      setReady(true);
    });
  };

  const convertMergeFieldsToLabels = ({ provider }) => {
    const allMfs = [...document.querySelectorAll(".merge-field")];
    if (allMfs?.length > 0) {
      allMfs.forEach(mf => {
        // remove highlighting effects
        mf.classList.remove("empty", "filled");

        const { mfType, mfSource, mfModule, mfLabel } = mf.dataset;
        if (mfType === "other" && mfSource === provider && thirdPartyModules) {
          const [, fieldLabel] = (mfLabel || "").split(".");

          const curModule = Array.isArray(thirdPartyModules)
            ? thirdPartyModules.find(r => r.api_name.toLowerCase() === mfModule.toLowerCase())
            : Object.values(thirdPartyModules).find(r => r?.api_name.toLowerCase() === mfModule.toLowerCase());

          // use plural_label if found
          mf.innerText =
            curModule?.plural_label && !_helpers.isDataEmpty(fieldLabel)
              ? `${curModule.plural_label}.${fieldLabel}`
              : _helpers.isDataEmpty(mfLabel)
              ? ""
              : mfLabel;
        } else {
          mf.innerText = mfLabel;
        }
      });
      requestAnimationFrame(() => {
        setReady(true);
      });
    } else {
      setReady(true);
    }
  };

  useEffect(() => {
    if (!["templates", "company-templates", "layout-templates"].includes(origin)) {
      const mfFlags = documentDetails?.merge_fields || documentDetails?.data?.merge_fields;
      const isDocumentSigned =
        ["declined", "partially_signed", "signed", "expired"].includes(documentDetails?.data?.proposal?.status) ||
        ["declined", "partially_signed", "signed", "expired"].includes(documentDetails?.proposal?.status);

      if (!mfFlags || isDocumentSigned) {
        if (documentDetails?.success && ["documents", "public"].includes(origin)) {
          setReady(true);
        }
        return;
      }
      // Run hydration only if merge_fields object exists
      // and at least one of the values is true
      const shouldHydrate = mfFlags && Object.values(mfFlags).some(flag => flag === true);
      if (!shouldHydrate) {
        setReady(true); // no hydration needed → mark ready immediately
        return;
      }

      const validAction = [PUBLISHED_DOCUMENT_FETCH_SUCCESS, DOCUMENT_DETAILS_FETCH_SUCCESS].includes(actionType);
      const documentId = documentDetails?.id || documentDetails?.data?.id;

      if (validAction && documentId && documentDetails?.success) {
        const fetchParams = {
          documentId,
          ...(userId && companyId && { extraParams: { userId, companyId } }),
        };
        dispatch(fetchDocumentMergeFields(fetchParams)).then(r => {
          if (r?.result?.success) firstFetchPostActions(r?.result?.merge_fields);
        });
      }
    }
  }, [actionType, documentDetails, origin]);

  useEffect(() => {
    if (
      ["templates", "company-templates", "layout-templates"].includes(origin) &&
      // thirdPartyModules &&
      (companyTemplateDetails || templateDetails || layoutDetails)
    ) {
      const provider = formatProviderDisplayName(
        companyTemplateDetails?.data?.data_source?.provider || templateDetails?.data_source?.provider
      );

      if ((provider && thirdPartyModules) || !provider)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            convertMergeFieldsToLabels({ provider }); // show hydrated labels
          });
        });
    }
  }, [origin, actionType, thirdPartyModules, companyTemplateDetails, templateDetails]);

  useEffect(() => {
    if (userId)
      dispatch(fetchUserSchema(userId)).then(r => {
        if (r.status === 200) setUserInfo(r.data);
      });
  }, [userId]);

  return { ready };
};

export default useMergeFieldsHydration;
